import { queryOptions, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES, QUERY_KEYS } from '@/config/constants';
import type { PlatformSettings, TopUpLimits } from '@/types/platform-settings';

const fetchPlatformSettings = async (): Promise<PlatformSettings> => {
  const { data } = await api.get<PlatformSettings>(API_ROUTES.pricing.settings);
  return data;
};

export const getPlatformSettingsQueryOptions = () =>
  queryOptions({
    queryKey: QUERY_KEYS.pricing.settings,
    queryFn: fetchPlatformSettings,
    // Platform-wide and changed only by a platform admin, so it does not need to be
    // re-fetched per billing interaction the way a balance does.
    staleTime: 5 * 60 * 1000,
  });

export const usePlatformSettings = () => useQuery(getPlatformSettingsQueryOptions());

/**
 * The top-up floor and ceiling, or `null` while they are still unknown.
 *
 * Deliberately `null` rather than a hardcoded default: enforcing a guessed range against a
 * slow or failed fetch would reject amounts the platform actually allows. Callers fall back
 * to "any positive amount" until the real limits arrive.
 */
export const useTopUpLimits = (): TopUpLimits | null => {
  const { data } = usePlatformSettings();
  if (data == null) return null;
  return { minTopUpUsd: data.minTopUp, maxTopUpUsd: data.maxTopUp };
};
